import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Spinner,
  Center,
  Text,
  HStack,
  IconButton,
  Input,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  SimpleGrid,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { supabase } from "../../../lib/supabaseClient";

const PAGE_SIZE = 30;

const PendingUserSettlement = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchName, setSearchName] = useState("");
  const [searchAccount, setSearchAccount] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [tempPage, setTempPage] = useState(page.toString());

  useEffect(() => {
    const fetchRows = async () => {
      setLoading(true);
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      let query = supabase
        .from("payout_buyer")
        .select(
          "id, bank_account_holder, amount_total, national_tax, local_tax, bank_name, bank_account_number, request_date, status, created_date, settlement_month",
          { count: "exact" }
        )
        .eq("status", "[User] Request")
        .order("created_date", { ascending: false })
        .range(from, to);
      if (searchName) {
        query = query.ilike("bank_account_holder", `%${searchName}%`);
      }
      if (searchAccount) {
        query = query.ilike("bank_account_number", `%${searchAccount}%`);
      }
      const { data, error, count } = await query;
      if (!error && Array.isArray(data)) {
        setRows(data);
        setTotal(count || 0);
      } else {
        setRows([]);
        setTotal(0);
      }
      setLoading(false);
    };
    fetchRows();
  }, [page, searchTrigger]);

  useEffect(() => {
    setTempPage(page.toString());
  }, [page]);

  const handleSearch = () => {
    setPage(1);
    setSearchTrigger((v) => v + 1);
  };

  const handleStatusChange = async () => {
    if (!selectedRow) return;

    try {
      const { error } = await supabase
        .from("payout_buyer")
        .update({
          status: "[Admin] Completed",
          payout_date: new Date().toISOString(),
        })
        .eq("id", selectedRow.id);

      if (error) throw error;

      toast({
        title: "정산 완료 처리되었습니다.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      // 목록 새로고침
      setSearchTrigger((v) => v + 1);
      onClose();
    } catch (error) {
      toast({
        title: "오류가 발생했습니다.",
        description: error.message,
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const handlePageInput = (e) => setTempPage(e.target.value);
  const handlePageMove = () => {
    if (/^\d+$/.test(tempPage)) {
      const p = parseInt(tempPage);
      if (p >= 1 && p <= totalPages) setPage(p);
      else setTempPage(page.toString());
    } else setTempPage(page.toString());
  };

  return (
    <Box p={4} bg="white">
      {/* 요약 정보 섹션 */}
      <Box mb={6} p={4} bg="blue.50" borderRadius="lg" boxShadow="sm">
        <SimpleGrid columns={2} spacing={4}>
          <Box>
            <Text fontSize="sm" color="gray.600" mb={1}>
              정산 대기 건수
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="blue.700">
              {total}건
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color="gray.600" mb={1}>
              세후 정산금 총액
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="blue.700">
              {rows
                .reduce((sum, row) => {
                  const nationalTax = Math.round(Number(row.national_tax) || 0);
                  const localTax = Math.round(Number(row.local_tax) || 0);
                  const amountTotal = Math.round(Number(row.amount_total) || 0);
                  const afterTax = Math.round(
                    amountTotal - (nationalTax + localTax)
                  );
                  return sum + afterTax;
                }, 0)
                .toLocaleString()}
              원
            </Text>
          </Box>
        </SimpleGrid>
      </Box>

      {/* 검색 UI */}
      <HStack mb={4} spacing={2}>
        <Input
          size="sm"
          width="180px"
          placeholder="이름 검색"
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSearch();
          }}
        />
        <Input
          size="sm"
          width="220px"
          placeholder="계좌번호 검색"
          value={searchAccount}
          onChange={(e) => setSearchAccount(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSearch();
          }}
        />
        <Button size="sm" colorScheme="blue" onClick={handleSearch}>
          검색
        </Button>
      </HStack>
      <Table
        size="sm"
        variant="simple"
        bg="white"
        borderRadius="lg"
        boxShadow="md"
        fontSize="sm"
      >
        <Thead bg="gray.50">
          <Tr>
            <Th fontSize="sm">정산월</Th>
            <Th fontSize="sm">이름</Th>
            <Th fontSize="sm">정산금</Th>
            <Th fontSize="sm">국세</Th>
            <Th fontSize="sm">지방세</Th>
            <Th fontSize="sm">세후 정산금</Th>
            <Th fontSize="sm">은행</Th>
            <Th fontSize="sm">계좌 번호</Th>
            <Th fontSize="sm">예금주</Th>
            <Th fontSize="sm">정산 신청일</Th>
            <Th fontSize="sm">진행 현황</Th>
          </Tr>
        </Thead>
        <Tbody>
          {loading ? (
            <Tr>
              <Td colSpan={11} fontSize="sm">
                <Center py={10}>
                  <Spinner size="lg" />
                </Center>
              </Td>
            </Tr>
          ) : rows.length === 0 ? (
            <Tr>
              <Td colSpan={11} fontSize="sm">
                <Text textAlign="center" color="gray.500" py={8} fontSize="sm">
                  데이터가 없습니다.
                </Text>
              </Td>
            </Tr>
          ) : (
            rows.map((row, idx) => {
              const nationalTax = Math.round(Number(row.national_tax) || 0);
              const localTax = Math.round(Number(row.local_tax) || 0);
              const amountTotal = Math.round(Number(row.amount_total) || 0);
              const afterTax = Math.round(
                amountTotal - (nationalTax + localTax)
              );
              return (
                <Tr key={idx} bg={idx % 2 === 0 ? "gray.50" : "white"}>
                  <Td fontSize="sm">{row.settlement_month || "-"}</Td>
                  <Td fontSize="sm">{row.bank_account_holder || "-"}</Td>
                  <Td fontSize="sm">{amountTotal.toLocaleString()}원</Td>
                  <Td fontSize="sm">{nationalTax.toLocaleString()}원</Td>
                  <Td fontSize="sm">{localTax.toLocaleString()}원</Td>
                  <Td fontSize="sm">{afterTax.toLocaleString()}원</Td>
                  <Td fontSize="sm">{row.bank_name || "-"}</Td>
                  <Td fontSize="sm">{row.bank_account_number || "-"}</Td>
                  <Td fontSize="sm">{row.bank_account_holder || "-"}</Td>
                  <Td fontSize="sm">
                    {row.request_date
                      ? new Date(row.request_date)
                          .toLocaleString("ko-KR", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          })
                          .replace(/\. /g, "-")
                          .replace(".", "")
                          .replace(/-(\d{2}:\d{2})/, " $1")
                      : "-"}
                  </Td>
                  <Td fontSize="sm">
                    <Button
                      size="sm"
                      w="100%"
                      bg="gray.200"
                      color="gray.700"
                      borderRadius="xl"
                      fontWeight="bold"
                      cursor="pointer"
                      _hover={{ bg: "gray.300" }}
                      _active={{ bg: "gray.400" }}
                      boxShadow="md"
                      onClick={() => {
                        setSelectedRow(row);
                        onOpen();
                      }}
                    >
                      {row.status === "[User] Request"
                        ? "정산 대기중"
                        : "정산 완료"}
                    </Button>
                  </Td>
                </Tr>
              );
            })
          )}
        </Tbody>
      </Table>
      {/* 페이지네이션 */}
      <HStack mt={4} justify="center" spacing={2}>
        <IconButton
          size="sm"
          aria-label="이전"
          icon={<ChevronLeftIcon />}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          isDisabled={page === 1}
        />
        <HStack spacing={1} mx={2}>
          <Input
            value={tempPage}
            onChange={handlePageInput}
            size="sm"
            width="40px"
            textAlign="center"
            fontSize="sm"
          />
          <Button
            size="sm"
            onClick={handlePageMove}
            colorScheme="gray"
            variant="outline"
            minW="32px"
            px={2}
            color="gray.600"
            borderColor="gray.300"
            _hover={{ bg: "gray.100", borderColor: "gray.400" }}
          >
            이동
          </Button>
          <Text fontSize="sm">/ {totalPages}</Text>
        </HStack>
        <IconButton
          size="sm"
          aria-label="다음"
          icon={<ChevronRightIcon />}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          isDisabled={page === totalPages}
        />
      </HStack>

      {/* 확인 모달 */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>정산 완료 처리</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              {selectedRow?.bank_account_holder}님의 정산을 완료
              처리하시겠습니까?
            </Text>
            <Text mt={2} fontSize="sm" color="gray.600">
              정산금: {selectedRow?.amount_total?.toLocaleString()}원
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="gray" mr={3} onClick={onClose}>
              취소
            </Button>
            <Button colorScheme="blue" onClick={handleStatusChange}>
              확인
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PendingUserSettlement;
